import type { HybridSearchPayload } from '@setplex/tria-api'
import type { ModuleFallbackFlags } from '~/shared/config/modules'

import { MediaTypes } from '@setplex/tria-api'
import { resolveSearchSection } from './sections'

/**
 * CR-409 BR-11…16: drop hybrid search hits whose Nora module is off.
 *
 * `undefined` is treated as enabled (`!== false`). The session DTO always
 * sends these flags after sign-in; classic search skins use truthiness
 * (`$flags/options.vodEnabled`) and are therefore stricter before session
 * load. Hybrid search keeps pre-session results and the existing suite
 * working, so only an explicit `false` hides an item.
 */
function isModuleOn(flag: boolean | undefined): boolean {
  return flag !== false
}

export function isHybridSearchItemAllowed(
  item: HybridSearchPayload,
  flags: ModuleFallbackFlags
): boolean {
  const section = resolveSearchSection(item)

  switch (section) {
    case 'LIVE_EVENTS':
      return isModuleOn(flags.liveEventEnabled)
    case 'WATCH_NOW':
      // AC-10: channel-only Watch Now still needs Live and EPG modules
      return isModuleOn(flags.tvEnabled) && isModuleOn(flags.epgEnabled)
    case 'CATCHUPS':
      if (!isModuleOn(flags.catchupEnabled) || !isModuleOn(flags.epgEnabled)) {
        return false
      }
      // Past live events in Catchups also need the Events module (FR-1.3)
      return (
        item.contentType !== MediaTypes.LIVE_EVENT ||
        isModuleOn(flags.liveEventEnabled)
      )
    case 'AVAILABLE':
      if (item.contentType === MediaTypes.VOD) {
        return isModuleOn(flags.vodEnabled)
      }
      if (item.contentType === MediaTypes.TV_SHOW) {
        return isModuleOn(flags.tvShowEnabled)
      }
      return true
    default:
      return true
  }
}
